import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';
import {
  Bell,
  BellOff,
  Calendar,
  AlertTriangle,
  Heart,
  MessageSquare,
  Users,
  Settings,
  Loader2,
  CheckCircle,
  XCircle,
  Smartphone,
  Info
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import usePushNotifications from '../../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_TYPES = [
  {
    key: 'task_reminders',
    icon: Calendar,
    iconClass: 'text-primary',
    label: 'Task Reminders',
    description: 'Reminders for upcoming pet care tasks',
    frequency: 'Typically 1–3 per day, depending on your pets',
  },
  {
    key: 'task_overdue',
    icon: AlertTriangle,
    iconClass: 'text-orange-500',
    label: 'Overdue Alerts',
    description: 'Alerts for tasks past their due date',
    frequency: 'Only when a task is missed — usually rare',
  },
  {
    key: 'adoption_updates',
    icon: Heart,
    iconClass: 'text-red-500',
    label: 'Adoption Updates',
    description: 'Updates about your adoption applications',
    frequency: 'A few per week during an active application',
  },
  {
    key: 'messages',
    icon: MessageSquare,
    iconClass: 'text-blue-500',
    label: 'Messages',
    description: 'New messages from other members',
    frequency: 'Arrives as soon as someone messages you',
  },
  {
    key: 'community_updates',
    icon: Users,
    iconClass: 'text-green-500',
    label: 'Community Updates',
    description: 'New posts in communities you follow',
    frequency: 'Can be several per day if you follow active communities',
  },
  {
    key: 'system_notifications',
    icon: Bell,
    iconClass: 'text-gray-500',
    label: 'System Notifications',
    description: 'Important platform announcements',
    frequency: 'Rare — roughly once a month',
  },
];

const NotificationSettings = () => {
  const { token } = useAuth();
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading: pushLoading,
    error: pushError,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications(token);

  const [preferences, setPreferences] = useState({
    task_reminders: true,
    task_overdue: true,
    adoption_updates: true,
    messages: true,
    community_updates: false,
    system_notifications: true
  });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);

  // Fetch preferences
  useEffect(() => {
    if (!token) return;

    const fetchPreferences = async () => {
      try {
        const [prefsRes, subsRes] = await Promise.all([
          axios.get(`${API_URL}/api/push-notifications/preferences`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/push-notifications/subscriptions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setPreferences(prefsRes.data);
        setSubscriptions(subsRes.data);
      } catch (err) {
        console.error('Failed to fetch notification settings:', err);
      }
    };

    fetchPreferences();
  }, [token]);

  // Handle preference change
  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);

    try {
      setPrefsLoading(true);
      await axios.put(
        `${API_URL}/api/push-notifications/preferences`,
        newPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Preferences updated');
    } catch (err) {
      toast.error('Failed to update preferences');
      setPreferences({ ...preferences }); // Revert
    } finally {
      setPrefsLoading(false);
    }
  };

  // Handle enable/disable notifications
  const handleToggleNotifications = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications disabled');
        setSubscriptions([]);
      }
    } else {
      const sub = await subscribe();
      if (sub) {
        toast.success('Notifications enabled!');
        // Refresh subscriptions
        const subsRes = await axios.get(`${API_URL}/api/push-notifications/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptions(subsRes.data);
      }
    }
  };

  // Send test notification
  const handleSendTest = async () => {
    const success = await sendTest();
    if (success) {
      toast.success('Test notification sent! Check your browser.');
    } else {
      toast.error('Failed to send test notification');
    }
  };

  const getPermissionStatus = () => {
    if (permission === 'granted') {
      return { icon: CheckCircle, text: 'Allowed', color: 'text-green-600' };
    }
    if (permission === 'denied') {
      return { icon: XCircle, text: 'Blocked', color: 'text-red-600' };
    }
    return { icon: Bell, text: 'Not set', color: 'text-yellow-600' };
  };

  const permStatus = getPermissionStatus();
  const PermIcon = permStatus.icon;

  if (!isSupported) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="w-5 h-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Your browser doesn't support push notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Try using a modern browser like Chrome, Firefox, Edge, or Safari to enable push notifications.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Toggle */}
      <Card data-testid="notification-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Receive browser notifications for important updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Permission Status */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <PermIcon className={`w-5 h-5 ${permStatus.color}`} />
              <span className="font-medium">Browser Permission: {permStatus.text}</span>
            </div>
            {permission === 'denied' && (
              <Badge variant="destructive">Blocked in browser settings</Badge>
            )}
          </div>

          {permission === 'denied' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <p className="font-medium text-red-800 mb-1">Notifications are blocked</p>
              <p className="text-red-700">
                To enable notifications, click the lock icon in your browser's address bar and allow notifications for this site.
              </p>
            </div>
          )}

          {/* Enable/Disable Button */}
          <div className="flex items-center gap-4">
            <Button
              onClick={handleToggleNotifications}
              disabled={pushLoading || permission === 'denied'}
              variant={isSubscribed ? 'outline' : 'default'}
              data-testid="toggle-notifications-btn"
            >
              {pushLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isSubscribed ? (
                <BellOff className="w-4 h-4 mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              {isSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
            </Button>

            {isSubscribed && (
              <Button variant="outline" onClick={handleSendTest} data-testid="test-notification-btn">
                Send Test
              </Button>
            )}
          </div>

          {pushError && (
            <p className="text-sm text-red-600">{pushError}</p>
          )}

          {/* Active Devices */}
          {subscriptions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Active Devices ({subscriptions.length})</p>
              <div className="space-y-2">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Smartphone className="w-4 h-4" />
                    <span>{sub.device_name}</span>
                    {sub.last_used_at && (
                      <span className="text-xs">
                        (Last used: {new Date(sub.last_used_at).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Types */}
      {isSubscribed && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Choose which notifications you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {NOTIFICATION_TYPES.map(({ key, icon: IconComp, iconClass, label, description, frequency }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IconComp className={`w-5 h-5 ${iconClass}`} />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor={key} className="font-medium">{label}</Label>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="text-gray-400 hover:text-gray-600 focus:outline-none"
                              aria-label={`Frequency info for ${label}`}
                              data-testid={`freq-info-${key}`}
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            {frequency}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>
                </div>
                <Switch
                  id={key}
                  checked={preferences[key]}
                  onCheckedChange={(v) => updatePreference(key, v)}
                  disabled={prefsLoading}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NotificationSettings;
